import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import CompanyDescriptionForm from '../components/analytic-profiles/CompanyDescriptionForm';
import { AnalyticProfilesList } from '../components/analytic-profiles/AnalyticProfilesList';
import { UserContext } from '../contexts/UserContext';

export type LayoutAnalyticProfilesProps = {
  /** When true, omit the company profile tab (e.g. when Company lives under Admin). */
  hideCompanyTab?: boolean;
};

type TabKind = 'company' | 'profiles';

export const LayoutAnalyticProfiles: React.FC<LayoutAnalyticProfilesProps> = ({
  hideCompanyTab = false,
}) => {
  const { user } = useContext(UserContext);
  const [selectedTab, setSelectedTab] = useState(0);

  const hasCompany = Boolean(!hideCompanyTab && user?.permissions?.company_profile?.update);
  const hasProfiles = Boolean(
    user?.permissions?.analytic_profiles?.read || user?.permissions?.analytic_profiles?.create
  );

  const tabDefs = useMemo(() => {
    const defs: { label: string; kind: TabKind }[] = [];
    if (hasCompany) defs.push({ label: 'Company profile', kind: 'company' });
    if (hasProfiles) defs.push({ label: 'Analytic profiles', kind: 'profiles' });
    return defs;
  }, [hasCompany, hasProfiles]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (selectedTab >= tabDefs.length) {
      setSelectedTab(0);
    }
  }, [selectedTab, tabDefs.length]);

  const activeKind = tabDefs[selectedTab]?.kind;
  const showCompanyPanel = hasCompany && activeKind === 'company';
  const showProfilesPanel = hasProfiles && activeKind === 'profiles';

  const showTabBar = tabDefs.length > 1;

  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          bgcolor: '#0a0a0a',
        }}
      >
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  if (tabDefs.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: '#0a0a0a', minHeight: '100vh' }}>
        <Typography sx={{ color: '#a1a1aa' }}>
          You do not have permission to view the company profile or analytic profiles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={{ backgroundColor: '#0a0a0a', minHeight: '100vh' }}>
      {showTabBar ? (
        <Box display="flex" alignItems="center" sx={{ pt: 0, px: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="Analytic profiles sections"
            sx={{
              flexGrow: 2,
              padding: 2,
              '& .MuiTab-root': {
                color: '#a1a1aa',
                '&.Mui-selected': {
                  color: '#fafafa',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#fafafa',
              },
            }}
          >
            {tabDefs.map((tab) => (
              <Tab key={tab.label} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      ) : null}
      <Box sx={{ px: 2, pt: showTabBar ? 0 : 2, pb: 2 }} flexGrow={1}>
        {hasCompany && (
          <Box sx={{ display: showCompanyPanel ? 'block' : 'none' }}>
            <CompanyDescriptionForm />
          </Box>
        )}
        {hasProfiles && (
          <Box sx={{ display: showProfilesPanel ? 'block' : 'none' }}>
            {hideCompanyTab ? <AnalyticProfilesList /> : <Outlet />}
          </Box>
        )}
      </Box>
    </Box>
  );
};
